"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  DataTable,
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  CheckList,
  type CheckListItem,
} from "@/components/tools/research-writing/check-list";
import { LengthMeter } from "@/components/tools/research-writing/length-meter";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  analyzeThreeMinute,
  BEATS,
  buildThreeMinuteDocument,
  emptySlide,
  formatClock,
  LIMIT_SECONDS,
  LONG_SENTENCE_WORDS,
  type PaceKey,
  SLIDE_ITEMS,
  SLIDE_RULES,
  type SlideItem,
  type ThreeMinuteInput,
  threeMinuteChecks,
  wordsToSeconds,
} from "@/lib/tool-kit/research-writing/three-minute-thesis";

/** English strings. A translated content file overrides these through `ui`. */
export const THREE_MINUTE_THESIS_PLANNER_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  talkHeading: "Your talk",
  titleLabel: "Thesis or talk title",
  titlePlaceholder: "e.g. Where Do People Wait for the Bus in the Sun?",
  paceLabel: "Speaking pace",
  paces: {
    deliberate: "Deliberate — 110 words per minute",
    conversational: "Conversational — 130 words per minute",
    brisk: "Brisk — 150 words per minute",
    custom: "My own pace",
  },
  paceHint:
    "A planning figure, not a rule: 3MT sets a time limit, not a word limit. Time yourself reading a paragraph aloud to find your own.",
  customLabel: "Your pace (words per minute)",
  customPlaceholder: "e.g. 140",
  marginLabel: "Safety margin (seconds)",
  marginHint:
    "Going over three minutes is disqualification under the UQ rules, so plan to finish early. 10 seconds is the default; choose your own.",
  scriptHeading: "Your script, beat by beat",
  scriptNote:
    "Write what you will say aloud, in sentences a non-specialist can follow. The tool counts your words and converts them to time at your pace.",
  beatPlaceholders: {
    hook: "Imagine … Every year, … Most people have never …",
    question: "What we do not know is … so my question is …",
    method: "To find out, I … I compared … with …",
    findings: "The result was … The biggest surprise was …",
    impact: "This matters because … So the next time you …",
  },
  slideHeading: "Your single slide",
  slideNote: "Tick each item as you check it against your slide.",
  slideItems: {
    single: "Only one slide.",
    static: "Static: no transitions, animations or movement of any kind.",
    media: "No sound, video or other electronic media.",
    props: "No props, costume or equipment needed to follow the talk.",
    start: "On screen from the first word.",
    legible: "Legible from the back of the room: few words, large type.",
    oneIdea: "One idea a stranger could take in within a few seconds.",
    adds: "Adds to what you say rather than repeating the script.",
  },
  ruleTag: "UQ rule",
  adviceTag: "advice",
  slideProgress: "{done} of {total} slide items ticked",
  meterHeading: "Timing and checks",
  meterLabel: "Speaking time against three minutes",
  meterText: "{time} of {limit} at {wpm} words per minute",
  wordsCount: "one: {count} word|other: {count} words",
  spareText: "{seconds} spare before your margin",
  overText: "{seconds} past your margin",
  timingHead: {
    beat: "Beat",
    target: "Target words",
    words: "Your words",
    time: "Your time",
  },
  timingTotal: "Total",
  checksLabel: "Checklist",
  checkStates: { done: "Done", todo: "To do", note: "Check" },
  checkText: {
    fits_done: "Fits within three minutes less your margin.",
    fits_todo: "Write something to time the script.",
    fits_tight:
      "Over your margin, but under three minutes. Trim a sentence or two so a slower delivery on the day still finishes inside the limit.",
    fits_over:
      "Over three minutes at this pace. Under the UQ rules a talk that runs over is disqualified: cut it down.",
    beats_done: "All five beats have text.",
    beats_todo: "Still to write: {beats}.",
    sentences_done: "No sentence is longer than {max} words.",
    sentences_note:
      "Your longest sentence has {longest} words. Long sentences are hard to follow when spoken: split anything over {max}.",
  },
  fileName: "three-minute-thesis",
  document: {
    heading: "Your 3MT script",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every beat has your words.",
  },
  defaultTitle: "Three Minute Thesis",
  subtitle:
    "Three Minute Thesis script · {time} of {limit} at {wpm} words per minute",
  beatHeadings: {
    hook: "1. Hook and motivation",
    question: "2. Background and research question",
    method: "3. What you did",
    findings: "4. What you found",
    impact: "5. Conclusions and impact",
  },
  beatTips: {
    hook: "Judges ask for clear motivation: why should a stranger care?",
    question:
      "Give only the background the question needs, then say the question.",
    method:
      "UQ asks whether you described your strategy or design: one plain picture beats a list of techniques.",
    findings:
      "Give the one result that matters most and what it means, in plain numbers.",
    impact:
      "Say what changes because of the work, then end on a line that returns to your opening.",
  },
  beatPrompts: {
    hook: "Open with one concrete image or fact, then say why it matters to someone outside your field: “Every year, [who] [suffers what].”",
    question:
      "State the background in a sentence or two, then the question: “What we do not know is [gap], so I asked [question].”",
    method:
      "Say what you did in everyday words: “To find out, I [did what] with [what or whom].”",
    findings:
      "State the main finding: “I found that [result], which means [meaning].” Use a number only if it will mean something to a listener.",
    impact:
      "Say what changes because of the result, and close on a line that echoes your opening.",
  },
  target: "Target: about {words} words ({time}).",
  yours: "You have {words} words ({time}).",
  timingHeading: "Timing",
  timingNote:
    "Time is words divided by your pace. It cannot know your pauses, your slide, or your nerves: rehearse aloud with a timer.",
  slideIntro:
    "Rules come from the University of Queensland’s competition rules; check the rules of your own event, which may add to them.",
  example: {
    title: "Where Do People Wait for the Bus in the Sun?",
    hook: "Imagine waiting for a bus at three in the afternoon in July. The shelter has no shade, the bench burns your legs, and the bus is late. For people who rely on the bus, that is not just an annoyance. It is a health risk, and almost nobody measures it. Heat is a hazard we can see coming, yet we plan for it at the scale of a city, not of a street corner.",
    question:
      "Cities plant trees and open cooling centres, but they decide where using citywide averages. An average hides the street corner where a person actually stands for twenty minutes. So my question is simple. Do the places where people wait for buses get less shade, and so more heat, than the streets around them? And if they do, how much hotter is it?",
    method:
      "To find out, I fixed coin-sized temperature sensors at [N] bus stops and at [N] matched spots a short walk away. They logged the temperature every ten minutes all summer. Then I photographed each spot at the same hour to measure the shade, and compared the two sets. Think of it as taking the temperature of the waiting, not of the city.",
    findings:
      "On the hottest afternoons, the bus stops were [X] degrees warmer than the spots nearby. [Describe, in everyday words, where the gap was largest and where it nearly vanished.] [Say what explained the difference, and how much of it.] I did not expect [name the surprise]. In short, [state the result in one plain sentence].",
    impact:
      "This matters because a city can build a shelter faster than it can grow a tree. If the pattern holds, a few shade structures, placed by measurement instead of guesswork, could make waiting safer for people with the fewest alternatives. So the next time you wait for a bus, look up. Is there any shade?",
  },
} as const;

const EMPTY: ThreeMinuteInput = {
  title: "",
  pace: "conversational",
  customWpm: "",
  margin: "",
  beats: { hook: "", question: "", method: "", findings: "", impact: "" },
  slide: emptySlide(),
};

const PACES: readonly PaceKey[] = [
  "deliberate",
  "conversational",
  "brisk",
  "custom",
];

export default function ThreeMinuteThesisPlannerWidget() {
  const strings = useToolUi(THREE_MINUTE_THESIS_PLANNER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<ThreeMinuteInput>(EMPTY);
  const analysis = useMemo(() => analyzeThreeMinute(input), [input]);
  const doc = useMemo(
    () => buildThreeMinuteDocument(input, strings, fmt),
    [input, strings, fmt]
  );
  const documentLabels = useMemo(() => strings.document, [strings]);

  const setBeat = (beat: (typeof BEATS)[number], value: string) =>
    setInput((previous) => ({
      ...previous,
      beats: { ...previous.beats, [beat]: value },
    }));
  const setSlide = (item: SlideItem, checked: boolean) =>
    setInput((previous) => ({
      ...previous,
      slide: { ...previous.slide, [item]: checked },
    }));
  const loadExample = () => {
    const e = strings.example;
    setInput({
      ...EMPTY,
      title: e.title,
      beats: {
        hook: e.hook,
        question: e.question,
        method: e.method,
        findings: e.findings,
        impact: e.impact,
      },
    });
  };

  const paceOptions = PACES.map((value) => ({
    value,
    label: strings.paces[value],
  }));
  const ticked = SLIDE_ITEMS.filter((item) => input.slide[item]).length;
  const timeText = t(strings.meterText, {
    time: formatClock(analysis.totalSeconds),
    limit: formatClock(LIMIT_SECONDS),
    wpm: fmt(analysis.wpm),
  });
  const seconds = fmt(Math.round(Math.abs(analysis.spare)));
  const balance =
    analysis.spare >= 0
      ? t(strings.spareText, { seconds: `${seconds} s` })
      : t(strings.overText, { seconds: `${seconds} s` });

  const checkText = strings.checkText as Record<string, string>;
  const checkItems: CheckListItem[] = threeMinuteChecks(analysis).map(
    (check) => ({
      key: check.id,
      state: check.state,
      text: t(checkText[check.textKey] ?? "", {
        longest: analysis.longest,
        max: LONG_SENTENCE_WORDS,
        beats: analysis.missing
          .map((beat) => strings.beatHeadings[beat])
          .join("; "),
      }),
    })
  );
  const rows = BEATS.map((beat) => [
    strings.beatHeadings[beat],
    fmt(analysis.targetWords[beat]),
    fmt(analysis.words[beat]),
    formatClock(wordsToSeconds(analysis.words[beat], analysis.wpm)),
  ]);
  rows.push([
    strings.timingTotal,
    fmt(analysis.budgetWords),
    fmt(analysis.totalWords),
    formatClock(analysis.totalSeconds),
  ]);

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.talkHeading}>
        <TextField
          id="tm-title"
          label={strings.titleLabel}
          onChange={(title) => setInput((p) => ({ ...p, title }))}
          placeholder={strings.titlePlaceholder}
          value={input.title}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            hint={strings.paceHint}
            id="tm-pace"
            label={strings.paceLabel}
            onChange={(pace) => setInput((p) => ({ ...p, pace }))}
            options={paceOptions}
            value={input.pace}
          />
          <TextField
            hint={strings.marginHint}
            id="tm-margin"
            label={strings.marginLabel}
            numeric
            onChange={(margin) => setInput((p) => ({ ...p, margin }))}
            placeholder={fmt(analysis.margin)}
            value={input.margin}
          />
        </div>
        {input.pace === "custom" ? (
          <TextField
            id="tm-custom"
            label={strings.customLabel}
            numeric
            onChange={(customWpm) => setInput((p) => ({ ...p, customWpm }))}
            placeholder={strings.customPlaceholder}
            value={input.customWpm}
          />
        ) : null}
      </Panel>

      <Panel title={strings.scriptHeading}>
        <p className={HINT}>{strings.scriptNote}</p>
        {BEATS.map((beat) => (
          <TextAreaField
            hint={`${fmt(analysis.words[beat])}${tPlural(strings.wordsCount, locale, analysis.words[beat])} · ${t(strings.target, { words: fmt(analysis.targetWords[beat]), time: formatClock(wordsToSeconds(analysis.targetWords[beat], analysis.wpm)) })}`}
            id={`tm-${beat}`}
            key={beat}
            label={strings.beatHeadings[beat]}
            onChange={(value) => setBeat(beat, value)}
            placeholder={strings.beatPlaceholders[beat]}
            prose
            rows={3}
            value={input.beats[beat]}
          />
        ))}
      </Panel>

      <Panel title={strings.slideHeading}>
        <p className={HINT}>{strings.slideNote}</p>
        <div className="space-y-2">
          {SLIDE_ITEMS.map((item) => (
            <Checkbox
              checked={input.slide[item]}
              key={item}
              label={`${strings.slideItems[item]} (${
                SLIDE_RULES.has(item) ? strings.ruleTag : strings.adviceTag
              })`}
              onChange={(checked) => setSlide(item, checked)}
            />
          ))}
        </div>
        <p aria-live="polite" className={HINT}>
          {t(strings.slideProgress, {
            done: fmt(ticked),
            total: fmt(SLIDE_ITEMS.length),
          })}
        </p>
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.meterHeading}</h2>
        <LengthMeter
          label={strings.meterLabel}
          max={LIMIT_SECONDS}
          over={analysis.state === "over"}
          value={Math.round(analysis.totalSeconds)}
          valueText={`${timeText} · ${balance}`}
        />
        <DataTable
          head={[
            strings.timingHead.beat,
            strings.timingHead.target,
            strings.timingHead.words,
            strings.timingHead.time,
          ]}
          rows={rows}
        />
        <CheckList
          items={checkItems}
          label={strings.checksLabel}
          stateLabels={strings.checkStates}
        />
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="tm"
        labels={documentLabels}
      />
    </div>
  );
}
